import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Box, Button, CircularProgress, FormControl, FormControlLabel,
  IconButton, InputLabel, MenuItem, Select, Switch, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';

import { useIsMobile } from '../../hooks/use-is-mobile';
import { useAllClubs } from '../../hooks/use-all-clubs';
import { useAuthContext } from '../../context/auth-context';
import { imprimirDocumento } from '../../utils/print-document';
import { construirListadoClubes, ordenarClubes, FILAS_POR_HOJA, ORDENES } from './club-list-report';

const plural = (n, singular, plural_) => `${n} ${n === 1 ? singular : plural_}`;

/**
 * Listado imprimible de los clubes inscritos en el campamento activo.
 *
 * El orden es la decisión del usuario, no una preferencia cosmética: alfabético sirve
 * para buscar un club en la mesa de inscripción, y por zona para repartir el papel a
 * los coordinadores. Por eso se elige antes de imprimir y viaja al informe, que numera
 * las filas ya ordenadas.
 */
export function ClubListDialog({ open, onClose }) {
  const isMobile = useIsMobile();
  const { activeCamp } = useAuthContext();
  const campId = activeCamp?._id ?? null;

  const { clubs, cargando, error } = useAllClubs(open, campId);

  const [orden, setOrden] = useState('alfabetico');
  const [incluirInactivos, setIncluirInactivos] = useState(false);
  const [errorImpresion, setErrorImpresion] = useState('');

  useEffect(() => {
    if (!open) {
      setOrden('alfabetico');
      setIncluirInactivos(false);
      setErrorImpresion('');
    }
  }, [open]);

  const seleccionados = useMemo(
    () => ordenarClubes(clubs.filter(club => incluirInactivos || club.status !== false), orden),
    [clubs, incluirInactivos, orden]
  );

  // Con orden por zona cada zona añade su fila de encabezado, y eso cambia el reparto.
  const zonas = useMemo(
    () => new Set(seleccionados.map(c => (typeof c.affiliatedTo === 'object' ? c.affiliatedTo?.zone : '') || '(sin zona)')),
    [seleccionados]
  );
  const filas = seleccionados.length + (orden === 'zona' ? zonas.size : 0);
  const hojas = Math.max(1, Math.ceil(filas / FILAS_POR_HOJA));

  const inactivos = clubs.filter(c => c.status === false).length;

  const imprimir = () => {
    setErrorImpresion('');
    const documento = construirListadoClubes({ camp: activeCamp, clubs: seleccionados, orden });
    if (!imprimirDocumento(documento)) {
      setErrorImpresion('El navegador bloqueó la ventana de impresión. Permite las ventanas emergentes e inténtalo de nuevo.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, fontWeight: 700 }}
      >
        Clubes inscritos
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Listado de los clubes inscritos en el campamento, con el logo y el nombre del campamento
          en la cabecera y las páginas numeradas. Desde el diálogo de impresión puedes guardarlo
          como PDF.
        </Typography>

        {!activeCamp && <Alert severity="info">Selecciona un campamento primero.</Alert>}

        {(error || errorImpresion) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || errorImpresion}
          </Alert>
        )}

        {activeCamp && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Orden</InputLabel>
                <Select value={orden} label="Orden" onChange={e => setOrden(e.target.value)}>
                  {ORDENES.map(o => (
                    <MenuItem key={o.valor} value={o.valor}>
                      {o.etiqueta}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={incluirInactivos}
                    onChange={e => setIncluirInactivos(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Incluir inactivos{inactivos ? ` (${inactivos})` : ''}
                  </Typography>
                }
              />
            </Box>

            <Typography variant="body2" color="text.secondary">
              {cargando
                ? 'Cargando clubes…'
                : `${plural(seleccionados.length, 'club', 'clubes')} · ${plural(hojas, 'página', 'páginas')} A4`}
            </Typography>

            {cargando && <CircularProgress size={20} sx={{ mt: 1 }} />}

            {!cargando && seleccionados.length === 0 && (
              <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                Sin clubes en este campamento.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={imprimir}
          disabled={cargando || seleccionados.length === 0}
          sx={{ bgcolor: '#0F6E56', '&:hover': { bgcolor: '#0a5a45' }, textTransform: 'none' }}
        >
          Imprimir / PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}
